/**
 * ui.toast.js
 * ToastComponent 单独文件 - 从 ui.js 中提取
 * 依赖于全局的 messageContainerManager
 */

class ToastComponent {
    constructor() {
        this.toasts = new Map();
        this.defaultOptions = {
            position: 'top-right',
            duration: 5000,
            type: 'info',
            html: false,
            title: '',
            message: '',
            showClose: true,
            autohide: true,
            animation: true
        };
        this.init();
    }

    init() {
        // Toast组件现在使用 messageContainerManager，无需预创建容器
    }

    show(message, options = {}) {
        const config = { ...this.defaultOptions, ...options };
        const { position, duration, type, title, showClose, autohide, animation, html } = config;

        const toastElement = this.createToastElement(message, type, title, showClose, autohide, animation, html);

        // 使用 messageContainerManager 添加到容器
        const added = messageContainerManager.addMessage(toastElement, position);

        if (added) {
            this.toasts.set(toastElement, config);

            // 显示动画
            setTimeout(() => {
                toastElement.classList.add('show');
            }, 10);

            // 自动隐藏
            if (autohide) {
                setTimeout(() => {
                    this.hide(toastElement);
                }, duration);
            }

            return toastElement;
        }

        return null;
    }

    success(message, options = {}) {
        return this.show(message, { ...options, type: 'success' });
    }

    error(message, options = {}) {
        return this.show(message, { ...options, type: 'error' });
    }

    warning(message, options = {}) {
        return this.show(message, { ...options, type: 'warning' });
    }

    info(message, options = {}) {
        return this.show(message, { ...options, type: 'info' });
    }

    createToastElement(message, type, title, showClose, autohide, animation, html) {
        const toast = document.createElement('div');
        toast.className = `toast toast-${type} ${animation ? '' : 'no-animation'}`;
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        toast.setAttribute('aria-atomic', 'true');

        if (autohide) {
            toast.setAttribute('data-bs-autohide', 'true');
        }

        let contentHtml = '';

        if (title) {
            contentHtml += `<div class="toast-header"> <strong class="me-auto">${title}</strong> ${showClose ? '<button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>' : ''} </div>`;
        }

        if (contentHtml) {
            contentHtml += `<div class="toast-body"> ${message} </div>`;
        } else {
            // escape message to avoid XSS when html not allowed
            const esc = String(message).replace(/[&<>"]+/g, function(s){
                return ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[s] || s);
            });
            contentHtml += `<div class="toast-body"> ${esc} </div>`;
        }

        toast.innerHTML = contentHtml;

        // 绑定关闭按钮事件
        if (showClose) {
            const closeBtn = toast.querySelector('.btn-close');
            if (closeBtn) {
                closeBtn.addEventListener('click', () => {
                    this.hide(toast);
                });
            }
        }

        return toast;
    }

    createContainerForPosition(position) {
        const container = document.createElement('div');
        container.className = `toast-container position-${position}`;
        container.setAttribute('aria-live', 'polite');
        container.setAttribute('aria-atomic', 'true');
        
        // 设置容器位置（样式应由 CSS 控制）
        this.setContainerPosition && this.setContainerPosition(container, position);
        
        document.body.appendChild(container);
        return container;
    }

    hide(toastElement) {
        if (!toastElement) return;

        toastElement.classList.remove('show');
        toastElement.classList.add('hide');

        setTimeout(() => {
            if (toastElement.parentNode) {
                toastElement.parentNode.removeChild(toastElement);
            }
            this.toasts.delete(toastElement);
        }, 300);
    }

    hideAll() {
        this.toasts.forEach((config, toastElement) => {
            this.hide(toastElement);
        });
    }

    static getInstance(element) {
        return new ToastComponent();
    }
}

// 静态包装，便于全局调用
class Toast {
    static _instance = new ToastComponent();
    static show(message, options) {
        return Toast._instance.show(message, options);
    }
    static success(message, options) {
        return Toast._instance.success(message, options);
    }
    static error(message, options) {
        return Toast._instance.error(message, options);
    }
    static warning(message, options) {
        return Toast._instance.warning(message, options);
    }
    static info(message, options) {
        return Toast._instance.info(message, options);
    }
    static hideAll() {
        return Toast._instance.hideAll();
    }
}

    // 立即将 Toast 暴露到全局（确保在 script 标签中立即可用）
    try {
        if (typeof window !== 'undefined') {
            window.Toast = window.Toast || Toast;
            globalThis.Toast = globalThis.Toast || Toast;
            // 兼容性别名
            window.toastInstance = window.toastInstance || Toast;
        }
    } catch (e) {
        // 在某些受限环境中可能失败，静默降级
    }

    function initializeToast() {
        // 初始化期间再次确保全局引用正确（安全覆盖）
        window.Toast = Toast;
        globalThis.Toast = Toast;
        window.toastInstance = Toast;
    }

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeToast);
} else {
    initializeToast();
}
